import {
  fixedFloat,
  fixedNumber,
  lerp,
  lerpMap,
  type Interpable,
  type Lerpable
} from '@utils';
import { composeArray, composeStruct, TextDirection } from '@strings';
import { z } from 'zod';
import {
  HAlignment,
  horizontalAlignmentForTextDirection,
  VAlignment,
  type Alignment
} from './Alignment';
import { SERIALIZATION_PRECISION } from './GeomConstants';
import type { PointObject } from './IPoint';
import type { RectLTWH, RectObject } from './IRect';
import { Point } from './Point';
import { Size } from './Size';

// #region Schema

export const RectSchema = z
  .preprocess(
    val => {
      if (typeof val === 'string') {
        return val.split(' ').map(parseFloat);
      }
    },
    z.tuple([z.number(), z.number(), z.number(), z.number()], {
      message: 'Rect must be a string of four numbers separated by spaces'
    })
  )
  .transform(([left, top, right, bottom]) => new Rect(left, top, right, bottom));

// #endregion

export class Rect implements RectObject, Lerpable, Interpable {
  readonly left: number;
  readonly top: number;
  readonly right: number;
  readonly bottom: number;

  // #region Constructors

  constructor(
    left:
      | number
      | [number, number, number, number]
      | Rect
      | Float32Array
      | [PointObject, PointObject]
      | { center: Point; size: Size }
      | RectObject
      | RectLTWH = 0,
    top?: number,
    right?: number,
    bottom?: number
  ) {
    if (
      typeof left === 'number' &&
      typeof top === 'number' &&
      typeof right === 'number' &&
      typeof bottom === 'number'
    ) {
      // left, top, right, and bottom are numbers
      this.left = left;
      this.top = top;
      this.right = right;
      this.bottom = bottom;
    } else if (Array.isArray(left)) {
      if (left.length === 4) {
        // left is an array of four numbers
        this.left = left[0];
        this.top = left[1];
        this.right = left[2];
        this.bottom = left[3];
      } else if (typeof left[0] === 'object' && typeof left[1] === 'object') {
        // left is an array of two points.
        // They can represent any two opposite corners of the rect.
        const x1 = left[0].x;
        const y1 = left[0].y;
        const x2 = left[1].x;
        const y2 = left[1].y;
        this.left = Math.min(x1, x2);
        this.top = Math.min(y1, y2);
        this.right = Math.max(x1, x2);
        this.bottom = Math.max(y1, y2);
      } else {
        throw new Error('Invalid arguments');
      }
    } else if (left instanceof Rect) {
      // left is a Rect
      this.left = left.left;
      this.top = left.top;
      this.right = left.right;
      this.bottom = left.bottom;
    } else if (left instanceof Float32Array && left.length === 4) {
      // left is a Float32Array of four numbers
      this.left = left[0];
      this.top = left[1];
      this.right = left[2];
      this.bottom = left[3];
    } else if (typeof left === 'object' && 'center' in left && 'size' in left) {
      // left is an object with center and size properties
      const halfWidth = left.size.width / 2;
      const halfHeight = left.size.height / 2;
      this.left = left.center.x - halfWidth;
      this.top = left.center.y - halfHeight;
      this.right = left.center.x + halfWidth;
      this.bottom = left.center.y + halfHeight;
    } else if (
      typeof left === 'object' &&
      'left' in left &&
      'top' in left &&
      'right' in left &&
      'bottom' in left
    ) {
      // left is a RectObject
      this.left = left.left;
      this.top = left.top;
      this.right = left.right;
      this.bottom = left.bottom;
    } else if (
      typeof left === 'object' &&
      'x' in left &&
      'y' in left &&
      'width' in left &&
      'height' in left
    ) {
      // left is a RectXYWH object
      this.left = left.x;
      this.top = left.y;
      this.right = left.x + left.width;
      this.bottom = left.y + left.height;
    } else if (typeof left === 'number') {
      // left is a single number (default value of 0) and top, right, and bottom are undefined
      this.left = left;
      this.top = left;
      this.right = left;
      this.bottom = left;
    } else {
      throw new Error('Invalid arguments');
    }

    Object.freeze(this);
  }

  static fromDOMRect(domRect: DOMRect): Rect | undefined {
    const r = new Rect(domRect.left, domRect.top, domRect.right, domRect.bottom);
    if (r.isValid) {
      return r;
    }
    return undefined;
  }

  /**
   * Creates a `Rect` instance that encompasses all the given points.
   *
   * @param points - An array of `Point` objects to be enclosed by the rectangle.
   * @returns A `Rect` object that bounds all the provided points.
   */
  static fromPoints(points: Point[]): Rect {
    let left = points[0].x;
    let top = points[0].y;
    let right = points[0].x;
    let bottom = points[0].y;
    for (const point of points) {
      left = Math.min(left, point.x);
      top = Math.min(top, point.y);
      right = Math.max(right, point.x);
      bottom = Math.max(bottom, point.y);
    }
    return new Rect(left, top, right, bottom);
  }

  /**
   * Creates a new `Rect` that encompasses all the given rectangles.
   *
   * @param rects - An array of `Rect` objects to be encompassed.
   * @returns A new `Rect` that covers the minimum left, top, and the maximum right, bottom values of the provided rectangles.
   */
  static fromRects(rects: Rect[]): Rect {
    let left = rects[0].left;
    let top = rects[0].top;
    let right = rects[0].right;
    let bottom = rects[0].bottom;
    for (const r of rects) {
      left = Math.min(left, r.left);
      top = Math.min(top, r.top);
      right = Math.max(right, r.right);
      bottom = Math.max(bottom, r.bottom);
    }
    return new Rect(left, top, right, bottom);
  }

  static fromCenterAndSize(center: Point, size: Size): Rect {
    const halfWidth = size.width / 2;
    const halfHeight = size.height / 2;
    return new Rect(
      center.x - halfWidth,
      center.y - halfHeight,
      center.x + halfWidth,
      center.y + halfHeight
    );
  }

  static ZERO = new Rect(0, 0, 0, 0);
  static INFINITE = new Rect(-Infinity, -Infinity, Infinity, Infinity);

  // #endregion

  // #region Attributes

  get isValid(): boolean {
    return (
      (this.left as unknown) != null &&
      (this.top as unknown) != null &&
      (this.right as unknown) != null &&
      (this.bottom as unknown) != null
    );
  }

  /**
   * Returns a new `Rect` instance with the coordinates rounded to the nearest integer values.
   * The `left` and `top` coordinates are rounded down using `Math.floor`,
   * while the `right` and `bottom` coordinates are rounded up using `Math.ceil`.
   *
   * @returns {Rect} A new `Rect` instance with integral coordinates.
   */
  get integral(): Rect {
    return new Rect(
      Math.floor(this.left),
      Math.floor(this.top),
      Math.ceil(this.right),
      Math.ceil(this.bottom)
    );
  }

  rounded(fractionDigits = 0): Rect {
    return new Rect(
      fixedFloat(this.left, fractionDigits),
      fixedFloat(this.top, fractionDigits),
      fixedFloat(this.right, fractionDigits),
      fixedFloat(this.bottom, fractionDigits)
    );
  }

  get width(): number {
    return this.right - this.left;
  }

  get height(): number {
    return this.bottom - this.top;
  }

  get size(): Size {
    return new Size(this.width, this.height);
  }

  get topLeft(): Point {
    return new Point(this.left, this.top);
  }

  get topRight(): Point {
    return new Point(this.right, this.top);
  }

  get bottomRight(): Point {
    return new Point(this.right, this.bottom);
  }

  get bottomLeft(): Point {
    return new Point(this.left, this.bottom);
  }

  get minXminY(): Point {
    return this.topLeft;
  }

  get maxXminY(): Point {
    return this.topRight;
  }

  get maxXmaxY(): Point {
    return this.bottomRight;
  }

  get minXmaxY(): Point {
    return this.bottomLeft;
  }

  get center(): Point {
    return new Point((this.left + this.right) / 2, (this.top + this.bottom) / 2);
  }

  // #endregion

  // #region Interpolation

  lerp(t: number, end: Rect): this {
    return new Rect(
      lerp(t, [this.left, end.left]),
      lerp(t, [this.top, end.top]),
      lerp(t, [this.right, end.right]),
      lerp(t, [this.bottom, end.bottom])
    ) as this;
  }

  interp(end: Rect): (t: number) => this {
    return (t: number) => this.lerp(t, end);
  }

  // #endregion

  // #region Comparison

  /**
   * Compares this rectangle to another rectangle for equality.
   *
   * @param other - The rectangle to compare with.
   * @returns `true` if the rectangles are equal, `false` otherwise.
   */
  equals(other: Rect): boolean {
    if (this === other) return true;
    return (
      this.left === other.left &&
      this.top === other.top &&
      this.right === other.right &&
      this.bottom === other.bottom
    );
  }

  /**
   * Returns true if the two optional rectangles are equal.
   *
   * - If both rectangles are undefined or the same instance, they are considered equal.
   * - If one rectangle is undefined and the other is not, they are considered unequal.
   * - If both rectangles are defined, they are compared for equality.
   *
   * @param a The first optional rectangle to compare.
   * @param b The second optional rectangle to compare.
   * @returns `true` if the two rectangles are equal, otherwise `false`.
   */
  static equals(a: Rect | undefined, b: Rect | undefined): boolean {
    if (a === b) return true;
    if (a === undefined || b === undefined) return false;
    return a.equals(b);
  }

  /**
   * Determines if this rectangle is close to another rectangle within a specified tolerance.
   *
   * @param other - The other rectangle to compare with.
   * @param tolerance - The tolerance within which the rectangles are considered close. Defaults to 1e-10.
   * @returns `true` if the rectangles are close within the specified tolerance, otherwise `false`.
   */
  isCloseTo(other: Rect, tolerance = 1e-10): boolean {
    return (
      Math.abs(this.left - other.left) <= tolerance &&
      Math.abs(this.top - other.top) <= tolerance &&
      Math.abs(this.right - other.right) <= tolerance &&
      Math.abs(this.bottom - other.bottom) <= tolerance
    );
  }

  // #endregion

  // #region Type Conversion

  get object(): RectObject {
    return {
      left: this.left,
      top: this.top,
      right: this.right,
      bottom: this.bottom
    };
  }

  get ltwh(): RectLTWH {
    return {
      x: this.left,
      y: this.top,
      width: this.width,
      height: this.height
    };
  }

  get array(): number[] {
    return [this.left, this.top, this.right, this.bottom];
  }

  get typedArray(): Float32Array {
    return new Float32Array([this.left, this.top, this.right, this.bottom]);
  }

  get ckRect(): Float32Array {
    return this.typedArray;
  }

  // #endregion

  // #region Immutable Setters

  withLeft(newLeft: number): Rect {
    return new Rect(newLeft, this.top, this.right, this.bottom);
  }

  withTop(newTop: number): Rect {
    return new Rect(this.left, newTop, this.right, this.bottom);
  }

  withRight(newRight: number): Rect {
    return new Rect(this.left, this.top, newRight, this.bottom);
  }

  withBottom(newBottom: number): Rect {
    return new Rect(this.left, this.top, this.right, newBottom);
  }

  // Holds width
  settingLeft(newLeft: number): Rect {
    return new Rect(newLeft, this.top, newLeft + this.width, this.bottom);
  }

  // Holds height
  settingTop(newTop: number): Rect {
    return new Rect(this.left, newTop, this.right, newTop + this.height);
  }

  // Holds width
  settingRight(newRight: number): Rect {
    return new Rect(newRight - this.width, this.top, newRight, this.bottom);
  }

  // Holds height
  settingBottom(newBottom: number): Rect {
    return new Rect(this.left, newBottom - this.height, this.right, newBottom);
  }

  // Holds left side
  settingWidth(newWidth: number): Rect {
    return new Rect(this.left, this.top, this.left + newWidth, this.bottom);
  }

  // Holds top side
  settingHeight(newHeight: number): Rect {
    return new Rect(this.left, this.top, this.right, this.top + newHeight);
  }

  // Holds bottom-right corner
  settingTopLeft(newTopLeft: Point): Rect {
    return new Rect(newTopLeft.x, newTopLeft.y, this.right, this.bottom);
  }

  // Holds bottom-left corner
  settingTopRight(newTopRight: Point): Rect {
    return new Rect(this.left, newTopRight.y, newTopRight.x, this.bottom);
  }

  // Holds top-left corner
  settingBottomRight(newBottomRight: Point): Rect {
    return new Rect(newBottomRight.x, this.top, this.right, newBottomRight.y);
  }

  // Holds top-right corner
  settingBottomLeft(newBottomLeft: Point): Rect {
    return new Rect(this.left, newBottomLeft.y, newBottomLeft.x, this.bottom);
  }

  // Holds maxXmaxY corner
  settingMinXMinY(newMinXMinY: Point): Rect {
    return this.settingTopLeft(newMinXMinY);
  }

  // Holds minXmaxY corner
  settingMaxXMinY(newMaxXMinY: Point): Rect {
    return this.settingTopRight(newMaxXMinY);
  }

  // Holds minXminY corner
  settingMaxXMaxY(newMaxXMaxY: Point): Rect {
    return this.settingBottomRight(newMaxXMaxY);
  }

  // Holds maxXminY corner
  settingMinXMaxY(newMinXMaxY: Point): Rect {
    return this.settingBottomLeft(newMinXMaxY);
  }

  // Holds size
  settingCenter(newCenter: Point): Rect {
    const halfWidth = this.width / 2;
    const halfHeight = this.height / 2;
    return new Rect(
      newCenter.x - halfWidth,
      newCenter.y - halfHeight,
      newCenter.x + halfWidth,
      newCenter.y + halfHeight
    );
  }

  // Holds size
  settingHorizontalCenter(newHorizontalCenter: number): Rect {
    const halfWidth = this.width / 2;
    return new Rect(
      newHorizontalCenter - halfWidth,
      this.top,
      newHorizontalCenter + halfWidth,
      this.bottom
    );
  }

  // Holds size
  settingVerticalCenter(newVerticalCenter: number): Rect {
    const halfHeight = this.height / 2;
    return new Rect(
      this.left,
      newVerticalCenter - halfHeight,
      this.right,
      newVerticalCenter + halfHeight
    );
  }

  // Holds center
  settingSize(newSize: Size): Rect {
    return Rect.fromCenterAndSize(this.center, newSize);
  }

  // #endregion

  // #region Geometric Operations

  /**
   * Expands the rect by the given amount on all sides.
   *
   * If the amount is positive, the rect grows outwards. If the amount is
   * negative, the rect shrinks inwards.
   *
   * @param delta The amount to expand the rect by.
   * @returns The expanded rect.
   */
  expandingBy(delta: number): Rect {
    const newLeft = Math.min(this.left - delta, this.right + delta);
    const newTop = Math.min(this.top - delta, this.bottom + delta);
    const newRight = Math.max(this.left - delta, this.right + delta);
    const newBottom = Math.max(this.top - delta, this.bottom + delta);
    return new Rect(newLeft, newTop, newRight, newBottom);
  }

  /**
   * Shrinks the rectangle by a specified delta.
   *
   * This method reduces the size of the rectangle by the given delta value.
   *
   * @param delta - The amount by which to shrink the rectangle.
   * @returns A new `Rect` instance with the reduced size.
   */
  shrinkingBy(delta: number): Rect {
    return this.expandingBy(-delta);
  }

  /**
   * Expands the rectangle to the minimum size if needed.
   *
   * @param minSize - The minimum size to which the rectangle should be expanded.
   * @returns A new `Rect` instance with the expanded size if the current size is smaller than the minimum size,
   *          otherwise returns the current `Rect` instance.
   */
  expandingToMinSize(minSize: Size): Rect {
    if (this.width >= minSize.width && this.height >= minSize.height) {
      return this;
    }
    return this.settingSize(this.size.max(minSize));
  }

  /**
   * Adjusts the current rectangle by the specified amounts.
   *
   * @param left - The amount to adjust the left side of the rectangle.
   * @param top - The amount to adjust the top side of the rectangle.
   * @param right - The amount to adjust the right side of the rectangle.
   * @param bottom - The amount to adjust the bottom side of the rectangle.
   * @returns A new `Rect` instance with the adjusted dimensions.
   */
  adjustingBy(left: number, top: number, right: number, bottom: number): Rect {
    return new Rect(
      this.left + left,
      this.top + top,
      this.right + right,
      this.bottom + bottom
    );
  }

  /**
   * Scales the rect around the given point by the given factor.
   */
  scalingAroundPoint(factor: number, p: Point): Rect {
    const newWidth = this.width * factor;
    const newHeight = this.height * factor;
    const dx = ((this.width - newWidth) * (p.x - this.left)) / this.width;
    const dy = ((this.height - newHeight) * (p.y - this.top)) / this.height;
    return new Rect(
      this.left + dx,
      this.top + dy,
      this.left + dx + newWidth,
      this.top + dy + newHeight
    );
  }

  /**
   * Calculates the scaling factor required to fit this rectangle within another rectangle.
   * The scaling factor is determined by the smaller ratio of the widths and heights of the two rectangles.
   *
   * @param frame - The rectangle within which this rectangle should fit.
   * @returns The scaling factor required to fit this rectangle within the other rectangle.
   */
  scaleThatFits(frame: Rect): number {
    return Math.min(frame.width / this.width, frame.height / this.height);
  }

  /**
   * Multiplies the dimensions of the rectangle by the given scalar.
   *
   * Effectively scales the rectangle by the given scalar factor.
   *
   * @param scalar - The number by which to multiply the rectangle's dimensions.
   * @returns A new `Rect` instance with dimensions scaled by the given scalar.
   */
  mul(scalar: number): Rect {
    return new Rect(
      this.left * scalar,
      this.top * scalar,
      this.right * scalar,
      this.bottom * scalar
    );
  }

  div(scalar: number): Rect {
    return new Rect(
      this.left / scalar,
      this.top / scalar,
      this.right / scalar,
      this.bottom / scalar
    );
  }

  /**
   * Adds the coordinates of a given point to the current rectangle and returns a new rectangle.
   *
   * Effectively translates the rectangle by the coordinates of the given point.
   *
   * @param p - The point whose coordinates will be added to the rectangle.
   * @returns A new `Rect` instance with updated coordinates.
   */
  add(p: Point): Rect {
    return new Rect(this.left + p.x, this.top + p.y, this.right + p.x, this.bottom + p.y);
  }

  sub(p: Point): Rect {
    return new Rect(this.left - p.x, this.top - p.y, this.right - p.x, this.bottom - p.y);
  }

  get area(): number {
    return this.width * this.height;
  }

  /**
   * Returns whether the rect contains the given point.
   */
  containsPoint(p: Point): boolean {
    return this.left <= p.x && p.x <= this.right && this.top <= p.y && p.y <= this.bottom;
  }

  /**
   * Returns the smallest rectangle that contains both this rect and the other rect.
   * If the other rect is null, returns this rect.
   */
  union(other?: Rect): Rect {
    if (!other) {
      return this;
    }
    return new Rect(
      Math.min(this.left, other.left),
      Math.min(this.top, other.top),
      Math.max(this.right, other.right),
      Math.max(this.bottom, other.bottom)
    );
  }

  /**
   * Expands the current rectangle to include the given point.
   * If the point is already within the rectangle, the current rectangle is returned.
   * Otherwise, a new rectangle is created that encompasses both the current rectangle and the given point.
   *
   * @param p - The point to include in the rectangle.
   * @returns A new rectangle that includes the given point, or the current rectangle if the point is already contained.
   */
  unionPoint(p: Point): Rect {
    if (this.containsPoint(p)) {
      return this;
    }
    return new Rect(
      Math.min(this.left, p.x),
      Math.min(this.top, p.y),
      Math.max(this.right, p.x),
      Math.max(this.bottom, p.y)
    );
  }

  /**
   * Returns the intersection of this rect with the other rect.
   * If the two rects do not intersect, returns null.
   */
  intersection(other: Rect): Rect | null {
    const left = Math.max(this.left, other.left);
    const top = Math.max(this.top, other.top);
    const right = Math.min(this.right, other.right);
    const bottom = Math.min(this.bottom, other.bottom);
    if (left < right && top < bottom) {
      return new Rect(left, top, right, bottom);
    } else {
      return null;
    }
  }

  /**
   * Returns true if this rect intersects the other rect.
   *
   * @param other - The rect to check for intersection with the current rect.
   * @returns `true` if the two rects intersect; otherwise, `false`.
   */
  intersects(other: Rect): boolean {
    return this.intersection(other) !== null;
  }

  /**
   * Checks if any pair of rectangles in the given array intersect with each other.
   *
   * @param rects - An array of `Rect` objects to be checked for intersections.
   * @returns `true` if any two rectangles intersect, otherwise `false`.
   */
  static anyIntersect(rects: Rect[]): boolean {
    for (let i = 0; i < rects.length; i++) {
      for (let j = i + 1; j < rects.length; j++) {
        if (rects[i].intersects(rects[j])) {
          return true;
        }
      }
    }
    return false;
  }

  /**
   * Determines whether the current rectangle completely contains another rectangle.
   *
   * @param other - The rectangle to check for containment within the current rectangle.
   * @returns `true` if the current rectangle contains the other rectangle; otherwise, `false`.
   */
  contains(other: Rect): boolean {
    return (
      this.left <= other.left &&
      this.top <= other.top &&
      this.right >= other.right &&
      this.bottom >= other.bottom
    );
  }

  /**
   * Maps a point from this rect to another rect.
   */
  mapPointToRect(p: Point, other: Rect): Point {
    return new Point(
      lerpMap(p.x, [this.left, this.right], [other.left, other.right]),
      lerpMap(p.y, [this.top, this.bottom], [other.top, other.bottom])
    );
  }

  /**
   * Aligns the top-left corner of the rectangle to the origin (0,0).
   *
   * @returns {Rect} A new rectangle instance with the top set to 0 and the left set to 0.
   */
  aligningTopLeftToOrigin(): Rect {
    return this.settingTop(0).settingLeft(0);
  }

  /**
   * Returns a new rect that is the result of aligning this rect relative to the given point.
   */
  alignedRelativeToPoint(p: Point, alignment: Alignment): Rect {
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    let result: Rect = this;

    if (alignment.h !== undefined) {
      const horizontalAlignment = horizontalAlignmentForTextDirection(
        alignment.h,
        alignment.textDirection ?? TextDirection.LTR
      );
      switch (horizontalAlignment) {
        case HAlignment.LEFT:
          result = this.settingLeft(p.x);
          break;
        case HAlignment.CENTER:
          result = this.settingHorizontalCenter(p.x);
          break;
        case HAlignment.RIGHT:
          result = this.settingRight(p.x);
          break;
        default:
          throw new Error('Invalid horizontal alignment');
      }
    }

    if (alignment.v !== undefined) {
      switch (alignment.v) {
        case VAlignment.TOP:
          result = result.settingTop(p.y);
          break;
        case VAlignment.CENTER:
          result = result.settingVerticalCenter(p.y);
          break;
        case VAlignment.BOTTOM:
          result = result.settingBottom(p.y);
          break;
        default:
          throw new Error('Invalid vertical alignment');
      }
    }

    return result;
  }

  /**
   * Returns a new rect that is the result of aligning this rect relative to the given rect.
   */
  alignedRelativeToRect(other: Rect, alignment: Alignment): Rect {
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    let result: Rect = this;

    if (alignment.h !== undefined) {
      const horizontalAlignment = horizontalAlignmentForTextDirection(
        alignment.h,
        alignment.textDirection ?? TextDirection.LTR
      );
      switch (horizontalAlignment) {
        case HAlignment.LEFT:
          result = this.settingLeft(other.left);
          break;
        case HAlignment.CENTER:
          result = this.settingHorizontalCenter(other.center.x);
          break;
        case HAlignment.RIGHT:
          result = this.settingRight(other.right);
          break;
        default:
          throw new Error('Invalid horizontal alignment');
      }
    }

    if (alignment.v !== undefined) {
      switch (alignment.v) {
        case VAlignment.TOP:
          result = result.settingTop(other.top);
          break;
        case VAlignment.CENTER:
          result = result.settingVerticalCenter(other.center.x);
          break;
        case VAlignment.BOTTOM:
          result = result.settingBottom(other.bottom);
          break;
        default:
          throw new Error('Invalid vertical alignment');
      }
    }

    return result;
  }

  /**
   * Returns a new rect that is the result of adjusting this rect so it is fully contained
   * in the given rect. If this rect is already contained, it is returned as is. If this rect
   * is larger than the given rect, it is centered within the given rect.
   */
  revealWithinRect(r: Rect): Rect {
    if (r.contains(this)) {
      return this;
    }

    let result = new Rect(this.left, this.top, this.right, this.bottom);

    // If this rect is larger than the given rect, center it within the given rect
    if (this.width > r.width || this.height > r.height) {
      return this.settingCenter(r.center);
    }

    // Adjust horizontally
    if (result.left < r.left) {
      result = result.settingLeft(r.left);
    } else if (result.right > r.right) {
      result = result.settingRight(r.right);
    }

    // Adjust vertically
    if (result.top < r.top) {
      result = result.settingTop(r.top);
    } else if (result.bottom > r.bottom) {
      result = result.settingBottom(r.bottom);
    }

    return result;
  }

  /**
   * Gets the corners of the rectangle.
   *
   * @returns {Point[]} An array of points representing the corners of the rectangle
   * in the following order: top-left, top-right, bottom-right, bottom-left.
   */
  get corners(): Point[] {
    return [this.topLeft, this.topRight, this.bottomRight, this.bottomLeft];
  }

  /**
   * Calculates the minimum delta vector by which this rectangle (the viewport)
   * should be translated to reveal the target rectangle. If the target is
   * already fully visible, the delta vector will be zero. If the target is
   * larger than this rectangle, the delta vector will be the amount required to
   * align the center of this rectangle with the center of the target rectangle.
   *
   * @param target - The target rectangle to reveal.
   */
  revealDelta(target: Rect): Point {
    // If target is fully contained, no movement needed
    if (this.contains(target)) {
      return new Point(0, 0);
    }

    // If target is larger than this rect, center this rect on target
    if (target.width > this.width && target.height > this.height) {
      return new Point(target.center.x - this.center.x, target.center.y - this.center.y);
    }

    let dx = 0;
    let dy = 0;

    // Calculate horizontal movement needed
    if (target.right > this.right) {
      dx = target.right - this.right;
    } else if (target.left < this.left) {
      dx = target.left - this.left;
    }

    // Calculate vertical movement needed
    if (target.bottom > this.bottom) {
      dy = target.bottom - this.bottom;
    } else if (target.top < this.top) {
      dy = target.top - this.top;
    }

    return new Point(dx, dy);
  }

  // #endregion

  // #region Serialization

  toJSON(): unknown {
    const left = fixedNumber(this.left, SERIALIZATION_PRECISION);
    const top = fixedNumber(this.top, SERIALIZATION_PRECISION);
    const right = fixedNumber(this.right, SERIALIZATION_PRECISION);
    const bottom = fixedNumber(this.bottom, SERIALIZATION_PRECISION);
    return `${left} ${top} ${right} ${bottom}`;
  }

  static fromJSON(json: unknown): Rect {
    return RectSchema.parse(json);
  }

  // #endregion

  // #region String Conversion

  /**
   * Formats the `Rect` as a string for debugging in {l, t, r, b} format.
   */
  toString(fractionDigits?: number): string {
    return composeStruct(
      [
        ['l', this.left],
        ['t', this.top],
        ['r', this.right],
        ['b', this.bottom]
      ],
      fractionDigits
    );
  }

  /**
   * Formats the `Rect` as a string for debugging in {l, t, w, h} format.
   */
  toStringLTWH(fractionDigits?: number): string {
    return composeStruct(
      [
        ['l', this.left],
        ['t', this.top],
        ['w', this.width],
        ['h', this.height]
      ],
      fractionDigits
    );
  }

  /**
   * Formats the `Rect` as a string for debugging in [l, t, w, h] format.
   */
  toStringArrayLTWH(fractionDigits?: number): string {
    return composeArray([this.left, this.top, this.width, this.height], fractionDigits);
  }

  /**
   * Formats the `Rect` as a string for debugging in {p1, p2} format.
   */
  toStringPoints(fractionDigits?: number): string {
    return composeStruct([
      ['p1', this.topLeft.toString(fractionDigits)],
      ['p2', this.bottomRight.toString(fractionDigits)]
    ]);
  }

  /**
   * Formats the `Rect` as a string for debugging in {center, size} format.
   */
  toStringCenterSize(fractionDigits?: number): string {
    return composeStruct([
      ['center', this.center.toString(fractionDigits)],
      ['size', this.size.toString(fractionDigits)]
    ]);
  }
}
